import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { AppState } from "../../../reducers";
import { Observable } from "rxjs";
import { CheckoutActions } from "../checkout.actions";
import { Customer, Address, DisplayShoppingCart, OrderDTO, DocumentsToPrint, Printer, BookingOffice,
	AvailableShipmentTypeDTO } from "../../../models"
import { MerchantApi, PrinterApi }   from "../../../services/webservice";
import { NgModel } from '@angular/forms';


@Component({
	selector: 'checkout-display-component',
	template: `
	<div class="container--margin-top">
		<checkout-display-presentation
					[customer]="customer$ | async"
					[shippingAddress]="shippingAddress$ | async"
					[billingAddress]="billingAddress$ | async"
					[paymentType]="paymentType$ | async"
					[shipmentType]="shipmentType$ | async"
					[errorMessages]="errorMessages$ | async"
					[displayShoppingCart]="displayShoppingCart$ | async"
					[ticketAddresses]="ticketAddresses$ | async"
					[totalValue]="shoppingCartTotal$ | async"
					[documentsToPrint]="documentsToPrint$ | async"
					[printerList]="printerList$ | async"
					[bookingOffice]="bookingOffice$ | async"
					[availableShipmentTypesInfo] = "availableShipmentTypesInfo$ | async"
					[availablePaymentTypes] = "availablePaymentTypes$ | async"
					[ticketPrinterId]="ticketPrinterId"
					[orderComment]="orderComment$ | async"

					(selectCustomerClicked)="selectCustomer()"
					(removeCustomerClicked)="removeCustomer()"
					(editCustomerClicked)="editCustomer($event)"
					(selectShippingAddressClicked)="selectShippingAddress($event)"
					(selectBillingAddressClicked)="selectBillingAddress($event)"
					(paymentTypeChange) = "paymentTypeChange($event)"
					(shipmentTypeChange) = "shipmentTypeChange($event)"
					(placeOrderClicked) = "placeOrder($event)"
					(documentsToPrintChange) = "documentsToPrintChange($event)"
					(printerChanged) = "printerChanged($event)"
					(returnToShoppingCart) = "returnToShoppingCart()"
				></checkout-display-presentation>
	</div>`,
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class CheckoutDisplayComponent implements OnInit {

	private customer$: Observable<Customer>
	private billingAddress$: Observable<Address>
	private shippingAddress$: Observable<Address>
	private paymentType$: Observable<string>
	private shipmentType$: Observable<string>
	private errorMessages$: Observable<any>
	private displayShoppingCart$: Observable<DisplayShoppingCart>;
	private ticketAddresses$: Observable<Record<string, Address>>;
	private shoppingCartTotal$: Observable<Number>;
	private documentsToPrint$: Observable<DocumentsToPrint>;
	private printerList$: Observable<Printer[]>;
	private bookingOffice$: Observable<BookingOffice>;
	private availableShipmentTypesInfo$: Observable<AvailableShipmentTypeDTO>;
	private availablePaymentTypes$: Observable<string[]>;
	private orderComment$: Observable<string>;
	private ticketPrinterId: string;
	private initialDocumetsToPrint: DocumentsToPrint = {
		ticketPrint: true,
		coverLetterPrint: false,
		invoicePrint: false
	}


	constructor(
		private store$: Store<AppState>,
		private route: ActivatedRoute,
		private router: Router,
		private checkoutActions: CheckoutActions,
		private merchantApi: MerchantApi,
		private printerApi: PrinterApi,
	) {
	}

	ngOnInit() {

		this.route.params.subscribe(params => {
			if (!params['fromCustomerSelection']) {
				this.initializeFormInputValues();
			}
		});

		this.customer$ = this.store$.select("checkout", "customer")
		this.billingAddress$ = this.store$.select("checkout", "billingAddress")
		this.shippingAddress$ = this.store$.select("checkout", "shippingAddress")
		this.paymentType$ = this.store$.select("checkout", "paymentType")
		this.shipmentType$ = this.store$.select("checkout", "shipmentType");
		this.errorMessages$ = this.store$.select("checkout", "errorMessages")
		this.displayShoppingCart$ = this.store$.select("shoppingCart", "displayShoppingCart")
		this.shoppingCartTotal$ = this.store$.select("shoppingCart", "displayShoppingCart").map(this.displayShoppingCartToTotal)
		this.ticketAddresses$ = this.store$.select("ticketAddress", "addresses")

		this.documentsToPrint$ = this.store$.select("checkout", "documentsToPrint")
		this.printerList$ = this.printerApi.getPrinters()
		this.ticketPrinterId = localStorage.getItem("lastUsedPrinter");
		this.store$.dispatch(this.checkoutActions.setTicketPrinter(this.ticketPrinterId))
		this.bookingOffice$ = this.merchantApi.getMyBookingOffice();
		this.availablePaymentTypes$ = this.bookingOffice$.map(bookingoffice => bookingoffice.activPaymentTypes);
		this.availableShipmentTypesInfo$ = this.store$.select("checkout", "availableShipmentTypesInfo");
		this.orderComment$ = this.store$.select("shoppingCart", "orderComment");

		Observable.combineLatest(this.paymentType$, this.availablePaymentTypes$, this.bookingOffice$,
			(selected, available, bookingOffice) => {
				if (selected == null || available.indexOf(selected) === -1) {
					if (available.indexOf(bookingOffice.defaultPaymentType) === -1) {
						return available[0];
					} else {
						return bookingOffice.defaultPaymentType;
					}
				} else {
					return null;
				}
			}
		).filter(i => i != null).subscribe((paymentType) => this.paymentTypeChange(paymentType));

		Observable.combineLatest(this.shipmentType$, this.availableShipmentTypesInfo$, this.bookingOffice$,
			(selected, available, bookingOffice) => {
				if (selected == null || (available && available.shipmentTypes.indexOf(selected) === -1)) {
					if (available && available.shipmentTypes.indexOf(bookingOffice.defaultShipmentType) === -1) {
						return available.shipmentTypes[0];
					}
					if (!available && bookingOffice.activShipmentTypes.indexOf(bookingOffice.defaultShipmentType) === -1) {
						return bookingOffice.activShipmentTypes[0];
					}
					return bookingOffice.defaultShipmentType;
				} else {
					return null;
				}
			}
		).filter(i => i != null).subscribe((shipmentType) => this.shipmentTypeChange(shipmentType));


	}

	editCustomer(customer: Customer) {
		this.router.navigate(["/customers", { fromSale: true }, customer.uuid, "basicdata", "edit"])
	}

	selectCustomer() {
		this.router.navigate(["/customers", { fromSale: true }])
	}

	selectBillingAddress(customer: Customer) {
		this.router.navigate(["/customers", { fromSale: true }, customer.uuid, { use: "billing" }, "addresses"])
	}
	selectShippingAddress(customer: Customer) {
		this.router.navigate(["/customers", { fromSale: true }, customer.uuid, { use: "shipping" }, "addresses"])
	}
	removeCustomer() {
		this.store$.dispatch(this.checkoutActions.removeCustomer())
	}

	paymentTypeChange(paymentType: string) {
		this.store$.dispatch(this.checkoutActions.setPaymentType(paymentType))
	}

	shipmentTypeChange(shipmentType: string) {
		this.store$.dispatch(this.checkoutActions.setShipmentType(shipmentType))
	}

	placeOrder(orderDTO: OrderDTO) {
		let ticketAddresses: Record<string, Address>;
		this.store$.select("ticketAddress", "addresses").take(1).subscribe(addresses => ticketAddresses=addresses).unsubscribe();
		if (ticketAddresses || Object.keys(ticketAddresses).length > 0) {
			orderDTO.ticketAddresses = ticketAddresses;
		}
		this.store$.dispatch(this.checkoutActions.placeOrder(orderDTO))
	}

	displayShoppingCartToTotal = (displayShoppingCart: DisplayShoppingCart): Number => {
		let total = 0;
		if (!displayShoppingCart || !displayShoppingCart.events) return 0;
		for (let event of displayShoppingCart.events) {
			for (let dt of event.displayTickets) {
				total += dt.subTotal.money.value
			}
		}
		return total;
	}

	documentsToPrintChange(documentsToPrint: DocumentsToPrint) {
		this.store$.dispatch(this.checkoutActions.setDocumentsToPrint(documentsToPrint))
	}

	printerChanged(printerId) {
		this.ticketPrinterId = printerId;
		localStorage.setItem("lastUsedPrinter", printerId);

		this.store$.dispatch(this.checkoutActions.setTicketPrinter(printerId))
	}

	returnToShoppingCart() {
		this.router.navigate(['/shopping-cart'])
	}

	private initializeFormInputValues() {
		this.shipmentTypeChange(null);
		this.paymentTypeChange(null);
		this.documentsToPrintChange(this.initialDocumetsToPrint)
	}
}



// WEBPACK FOOTER //
// ./src/app/components/checkout-components/checkout-display/checkout-display.component.ts